import React, { useContext } from 'react'; 
import { 
  Box, Typography, IconButton, Badge, Avatar, 
  Button, alpha, InputBase 
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom'; 

// Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search'; 
import MenuIcon from '@mui/icons-material/Menu'; 

// Contexts & Components
import AuthContext from "./auth/Context";
import NotificationContext from "./auth/NotificationContext"; 
import LockScreen from "./LockScreen";
import useAuthGate from "./useAuthGate";
import useScrollRestoration from './useScrollRestoration'; 

// IMPORT CONTEXT TỪ APP LAYOUT ĐỂ LẤY NÚT TOGGLE MENU MOBILE
import { AppLayoutContext } from './AppLayout'; 

export default function PageContainer({
  title,
  onBack,
  requireLogin = null, 
  showMessages = true,
  showNotifications = true,
  showProfile = true,
  extraActions = null,
  contentSx = {}, 
  containerSx={},
  hideHeader = false,
  onSearch, 
  placeholderSearch = "Tìm kiếm...",
  scrollReady = true, 
  children
}) {
  const context = useContext(AuthContext);
  const notiContext = useContext(NotificationContext);
  const layoutContext = useContext(AppLayoutContext); // Gọi context để điều khiển Sidebar
  
  const history = useHistory();
  const location = useLocation(); 

  const unreadNotifications = notiContext?.notifications || 0;
  const unreadMessages = notiContext?.messages || 0;
  
  // ── TRẠNG THÁI XÁC THỰC ──────────────────────────────────────────────────────────────────────
  // Bản cũ chỉ nhìn `userInfo` nên không phân biệt được "chưa đăng nhập" với "đang đăng nhập /
  // đang chọn công ty" -> LockScreen nằm phía sau hộp thoại đăng nhập và hộp chọn công ty.
  // Nay đọc PHA XÁC THỰC từ Provider: đang bận thì hiện trạng thái chờ, đã đăng nhập mà thiếu công
  // ty thì mời chọn công ty, chỉ khi thực sự chưa đăng nhập mới hiện màn yêu cầu đăng nhập.
  // Dùng CHUNG hook với ReportFrame/DataListManager để ba nơi không bao giờ lệch nhau nữa.
  const authGate = useAuthGate(requireLogin);
  const { isLoggedIn, authBusy } = authGate;
  const isPublicUser = authGate.isPublicUser;
  const isShowingLockScreen = authGate.blocked;

  let avatarSrc = '/images/avatar.png';
  if (context.userInfo?.picture) {
    const pic = context.userInfo.picture;
    if (typeof pic === 'string') {
        avatarSrc = pic.startsWith("http") ? pic : `${context.config?.server_url || ''}${pic}`;
    }
  }

  // Props cho LockScreen (autoSignin/canCancel/variant) do useAuthGate tính sẵn — và nó tạo BẢN SAO
  // của object `requireLogin`, không mutate props của component cha như bản cũ.
  const lockScreenProps = authGate.lockProps;

  // KÍCH HOẠT HOOK SCROLL RESTORATION
  const { scrollRef, saveScroll } = useScrollRestoration({
    key: location.pathname,             
    isReady: scrollReady,               
    condition: !isShowingLockScreen     
  });

  return (
    <Box sx={{display:"flex",flexDirection:"column",height:"100%",...containerSx}}>
      {/* HEADER */}
      {!hideHeader && <Box 
        sx={{ 
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.95), 
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          color: 'primary.contrastText',
          pt: 'max(12px, env(safe-area-inset-top))', 
          pb: 1.5, px: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 1100
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden', mr: 1 }}>
          
          {/* NÚT MENU MOBILE: Chỉ hiện khi KHÔNG CÓ nút Back (ưu tiên nút Back) hoặc không phải là agent */}
          {layoutContext && !context.queryParams?.for_agent && !onBack && (
            <IconButton 
              onClick={layoutContext.handleDrawerToggle} 
              sx={{ color: 'inherit', mr: 0.5, p: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* NÚT BACK: Hiển thị nếu được truyền prop onBack */}
          {onBack && (
            <IconButton onClick={onBack} sx={{ color: 'inherit', mr: 0.5, p: 1, flexShrink: 0 }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          {onSearch ? (
            <Box sx={{flex:1,display: 'flex', alignItems: 'center'}}>
              <Box 
                  sx={{ 
                      width: "100%",maxWidth:"300px", display: 'flex', alignItems: 'center', 
                      bgcolor: 'rgba(255, 255, 255, 0.15)', borderRadius: 2, px: 1.5, 
                      transition: 'background-color 0.2s', '&:focus-within': { bgcolor: 'rgba(255, 255, 255, 0.25)' }
                  }}
              >
                  <SearchIcon sx={{ fontSize: 20, mr: 1, opacity: 0.8 }} />
                  <InputBase
                      placeholder={placeholderSearch}
                      onChange={(e) => onSearch(e.target.value)}
                      sx={{ color: 'inherit', width: '100%', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.95rem', '&::placeholder': { color: 'rgba(255, 255, 255, 0.6)', opacity: 1 } } }}
                  />
              </Box>
            </Box>
          ) : (
            <Typography variant="h6" fontWeight="bold" noWrap sx={{ fontSize: '1.15rem', pl: onBack && !layoutContext ? 0 : 1.5 }}>
              {title || context.config?.app_name || "App"}
            </Typography>
          )}
        </Box>

        {/* CÁC NÚT BÊN PHẢI HEADER */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {isLoggedIn ? (
            <>
              {extraActions && <Box sx={{ display: 'flex', alignItems: 'center' }}>{extraActions}</Box>}
              {showMessages && (
                <IconButton onClick={() => history.push('/chat')} sx={{ color: 'inherit', p: 1 }}>
                  <Badge badgeContent={unreadMessages} color="error" max={99}><ChatBubbleOutlineIcon sx={{ fontSize: 22 }} /></Badge>
                </IconButton>
              )}
              {showNotifications && (
                <IconButton onClick={() => history.push('/notifications')} sx={{ color: 'inherit', p: 1 }}>
                  <Badge badgeContent={unreadNotifications} color="error" max={99}><NotificationsOutlinedIcon sx={{ fontSize: 24 }} /></Badge>
                </IconButton>
              )}
              {showProfile && (
                <IconButton onClick={() => history.push('/profile')} sx={{ p: 0.5, ml: 0.5 }}>
                  <Avatar src={avatarSrc} alt={context.userInfo?.name || 'User'} sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}>
                    {!context.userInfo?.picture && <PersonIcon sx={{ color: 'inherit', fontSize: 20 }} />}
                  </Avatar>
                </IconButton>
              )}
            </>
          ) : authBusy ? (
            // Đang khởi động/đăng nhập: để trống thay vì nháy cặp nút Đăng nhập/Đăng ký rồi mất ngay.
            null
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, pr: 0.5 }}>
              <Button size="small" variant="text" startIcon={<LoginIcon sx={{ fontSize: '1.2rem !important' }}/>} onClick={() => context.signin && context.signin()} sx={{ color: 'inherit', textTransform: 'none', fontWeight: 'bold', px: 1, minWidth: 0, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 }, ml: {xs: 0, sm: -0.5} } }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Đăng nhập</Box>
              </Button>
              <Button size="small" variant="contained" startIcon={<PersonAddAlt1Icon sx={{ fontSize: '1.1rem !important' }}/>} onClick={() => context.signup && context.signup()} sx={{ bgcolor: 'white', color: 'primary.main', textTransform: 'none', fontWeight: 'bold', borderRadius: 2, px: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#f5f5f5', boxShadow: 'none' }, minWidth: 0, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 }, ml: {xs: 0, sm: -0.5} } }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Đăng ký</Box>
              </Button>
            </Box>
          )}
        </Box>
      </Box>}

      {/* CONTENT */}
      <Box 
        ref={scrollRef}               
        onScroll={saveScroll}         
        sx={{ 
          flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', 
          pb: 'env(safe-area-inset-bottom)',
           ...contentSx 
        }}
      >
        {isShowingLockScreen ? (
            <LockScreen {...lockScreenProps} />
        ) : (
            children
        )}
      </Box>
    </Box>
  );
}